import { Injectable } from '@angular/core';
import { CollarsPositionDto } from '../../shell-communication/collars-operations';
import { isVoid, makeCollarsOptionDisplayName } from '../../utils';
import { parseOptionTicker } from '../../options-common/options.model';
import {
   CollarsDiffResult,
   CollarsEditableLeg,
   CollarsAfterStateLeg,
   CollarsGroupTransaction,
   CollarsTransactionDelta
} from './collars-after-state.models';

/**
 * The SAME diff service backs the Make-Transaction dialog Preview AND Apply After State. Given the scope's
 * current live positions and the edited/saved end-state legs, it produces the read-only Before / Transaction
 * / After view and re-derives each delta's action (Open / Close / Increase / Decrease) from the pair.
 *
 * It mirrors the Hedging Grid after-state preview (hg-after-state-preview.component.ts setupRows()): match
 * current vs end-state per instrument (new => Open, removed => Close, qty delta => Increase/Decrease, side
 * change => Close+Open), then compose the three columns. Unlike HG, the unit of comparison here is one
 * (group, instrument) pair (HG groups by hedge group only, one leg per group); the rule set below makes the
 * per-instrument matching explicit.
 *
 * The edit primitives in the dialog shape the END-STATE rows only; this service is the single re-derivation
 * point that turns (current, end-state) into Open/Close/Increase/Decrease. Side-flip / strike-or-expiry
 * changes are already expressed by the dialog as clone-and-remove (a crossed-out original leg + a new leg);
 * a crossed-out leg is dropped by projectAfter(), so it simply disappears from the end-state and falls out
 * naturally as a Close (old) here, while the new leg falls out as an Open. A direct opposite-side leg on the
 * SAME instrument (a reverse) is handled by rule 4 below: Close the current then Open the end-state side.
 */
@Injectable()
export class CollarsTransactionDiffService {

   /**
    * Compute the three-column diff for a scope.
    *
    * @param currentPositions live positions scoped to the bucket (Group, or all leaf groups under a
    *                         Combo/Portfolio); the "Before" face value.
    * @param editableLegs     the edited end-state rows from the dialog (Make-Transaction), or the saved
    *                         end-state projected to editable rows (Apply).
    * @param scopeBucketId    Portfolio / Combo / Group bucket id the operator opened the dialog on.
    * @param level            0 = Portfolio, 1 = Combo, 2 = Group.
    */
   computeDiff(
      currentPositions: CollarsPositionDto[],
      editableLegs: CollarsEditableLeg[],
      scopeBucketId: string,
      level: number
   ): CollarsDiffResult {

      const current = currentPositions || [];
      const editable = editableLegs || [];

      // --- Validation: the end-state must hold only positive magnitudes (zero/negative are BLOCKED). ---
      // Mirrors the Hedging Grid block: any in-scope (not crossed-out) leg with a non-positive qty/targetQty
      // invalidates the whole diff so the dialog can refuse Save/Commit/Apply and show the reason.
      const invalidLeg = editable.find(l =>
         !l.crossedOut && (this.endStateQty(l) <= 0));

      if (!isVoid(invalidLeg)) {
         return {
            before: this.projectBefore(current),
            transaction: [],
            after: [],
            isValid: false,
            // blockReason is USER-FACING (toasts + the Apply dialog's blocked section) — show the display
            // name, never the raw composed ticker (stocks exempt: raw == display).
            blockReason: `Quantity must be greater than zero (${this.legDescription(invalidLeg.tradingInstrumentCode)}).`
         };
      }

      // --- The three columns. ---
      const before = this.projectBefore(current);
      const after = this.projectAfter(editable);
      const transaction = this.deriveTransaction(current, editable);

      return { before, transaction, after, isValid: true };
   }

   /**
    * Project the editable rows down to the persisted end-state legs (drops crossed-out rows, normalises to a
    * positive qty). This is what Save writes to user settings and what the After column renders.
    */
   projectAfter(editableLegs: CollarsEditableLeg[]): CollarsAfterStateLeg[] {
      const editable = editableLegs || [];
      return editable
         .filter(l => !l.crossedOut)
         .map(l => {
            const leg: CollarsAfterStateLeg = {
               groupBucketId: l.groupBucketId,
               tradingInstrumentCode: l.tradingInstrumentCode,
               kind: l.kind,
               side: l.side,
               qty: Math.abs(this.endStateQty(l))
            };
            if (!isVoid(l.optionType)) { leg.optionType = l.optionType; }
            if (this.isPresent(l.strike)) { leg.strike = l.strike; }
            if (!isVoid(l.expiration)) { leg.expiration = l.expiration; }
            return leg;
         });
   }

   // --- internals ---------------------------------------------------------------------------------------

   // User-facing instrument description for validation messages: option codes render as the Collars
   // friendly name (composed via the pure utils helper — no display-name service in this model-layer
   // class, so the underlying shows as its raw token, which equals the display name for stock-like
   // underlyings); asset codes stay raw (exempt: raw == display).
   private legDescription(tradingInstrumentCode: string): string {
      if (isVoid(tradingInstrumentCode)) { return ''; }
      const t = parseOptionTicker(tradingInstrumentCode);
      return t ? makeCollarsOptionDisplayName(t) : tradingInstrumentCode;
   }

   /**
    * The end-state magnitude a row resolves to: targetQty when the dual input set one, else qty. Uses a
    * strict null/undefined check (NOT isVoid) on purpose — isVoid(0) is true for numbers, which would mask a
    * zero targetQty and skip the zero-is-BLOCKED validation. A targetQty of 0 must survive to be rejected.
    */
   private endStateQty(leg: CollarsEditableLeg): number {
      return (leg.targetQty === null || leg.targetQty === undefined) ? leg.qty : leg.targetQty;
   }

   /** Strict presence: only null/undefined are absent (0 and '' are present). For numeric optionals. */
   private isPresent(value: any): boolean {
      return value !== null && value !== undefined;
   }

   /** The diff comparison key: a position is the SAME line when group + instrument match. */
   private positionKey(groupBucketId: string, tradingInstrumentCode: string): string {
      return (groupBucketId || '') + '|' + (tradingInstrumentCode || '');
   }

   /** Side of a current OPEN position from the sign of its net (positive => Buy/long, negative => Sell). */
   private sideOfNet(netPosition: number): number {
      return netPosition >= 0 ? 1 : -1;
   }

   /** The open, real positions in scope (exclude flat, Interest, and archived rows). */
   private openPositions(currentPositions: CollarsPositionDto[]): CollarsPositionDto[] {
      return (currentPositions || []).filter(p =>
         p.netPosition !== 0 && !p.isInterest && !p.isArchived);
   }

   /** Render the live current positions as Before rows (display only — these are never edited). */
   private projectBefore(currentPositions: CollarsPositionDto[]): CollarsEditableLeg[] {
      let seq = 0;
      return this.openPositions(currentPositions).map(p => {
         const leg: CollarsEditableLeg = {
            id: ++seq,
            isNew: false,
            crossedOut: false,
            restorable: true,
            portfolioId: p.portfolioId || undefined,
            comboId: p.comboId || undefined,
            groupBucketId: p.groupId || '',
            kind: p.kind,
            tradingInstrumentCode: p.tradingInstrumentCode,
            side: this.sideOfNet(p.netPosition),
            qty: Math.abs(p.netPosition),
            originalQty: Math.abs(p.netPosition),
            targetQty: Math.abs(p.netPosition),
            liveQuote: p.lastMatchPx
         };
         return leg;
      });
   }

   /**
    * Re-derive the per-group ordered deltas from (current OPEN positions, end-state legs), keyed by
    * group + instrument. Implements the DIFF MAPPING precisely:
    *   1) in end-state, not in current                       -> Open(end side, end qty)
    *   2) in current, not in end-state (or crossed-out)       -> Close(opposite side, |net|)
    *   3) in both, same side, |end| > |cur|                   -> Increase by the diff (end side)
    *      in both, same side, |end| < |cur|                   -> Decrease by the diff (end side)
    *      in both, same side, equal                           -> no delta
    *   4) in both, opposite side (reverse)                    -> Close(current) THEN Open(end opposite)
    * Roll (old ticker absent + new ticker present) falls out of 1 + 2 naturally.
    */
   private deriveTransaction(
      currentPositions: CollarsPositionDto[],
      editableLegs: CollarsEditableLeg[]
   ): CollarsGroupTransaction[] {

      const open = this.openPositions(currentPositions);

      // End-state legs that survive into the desired state (crossed-out rows are excluded).
      const endLegs = (editableLegs || []).filter(l => !l.crossedOut);

      // Index current and end-state by group + instrument. (One position per key is expected; if duplicates
      // exist we take the first deterministically — the index is a 1:1 line model.)
      const currentByKey = new Map<string, CollarsPositionDto>();
      open.forEach(p => {
         const key = this.positionKey(p.groupId || '', p.tradingInstrumentCode);
         if (!currentByKey.has(key)) { currentByKey.set(key, p); }
      });

      const endByKey = new Map<string, CollarsEditableLeg>();
      endLegs.forEach(l => {
         const key = this.positionKey(l.groupBucketId, l.tradingInstrumentCode);
         if (!endByKey.has(key)) { endByKey.set(key, l); }
      });

      // Collect deltas keyed by leaf group, preserving emission order (Close-before-Open for a reverse).
      const deltasByGroup = new Map<string, CollarsTransactionDelta[]>();
      const groupOrder: string[] = [];

      const pushDelta = (groupBucketId: string, delta: CollarsTransactionDelta) => {
         if (!deltasByGroup.has(groupBucketId)) {
            deltasByGroup.set(groupBucketId, []);
            groupOrder.push(groupBucketId);
         }
         deltasByGroup.get(groupBucketId).push(delta);
      };

      // Walk current positions: matched (both), reversed, increased/decreased, or closed (removed).
      open.forEach(p => {
         const key = this.positionKey(p.groupId || '', p.tradingInstrumentCode);
         const groupBucketId = p.groupId || '';
         const end = endByKey.get(key);
         const curSide = this.sideOfNet(p.netPosition);
         const curQty = Math.abs(p.netPosition);

         if (isVoid(end)) {
            // Rule 2: present in current, absent from end-state -> Close the whole line (opposite side).
            pushDelta(groupBucketId, this.makeDelta(p.groupId || '', p, 'Close', curSide * -1, curQty));
            return;
         }

         const endSide = end.side;
         const endQty = Math.abs(this.endStateQty(end));

         if (endSide === curSide) {
            // Rule 3: same side -> Increase / Decrease by the magnitude diff (no delta when equal).
            // delta.side is the TRADE side everywhere downstream (booking nets qty*side, the TOS copy
            // keys Buy/Sell off it, the preview shows it as the qty sign) — shrinking a holding trades
            // OPPOSITE to it (long 10->6 = Sell 4), so Decrease flips like Close does. Until 2026-06-11
            // it carried the holding side, so applying a Decrease booked the position BIGGER.
            const diff = endQty - curQty;
            if (diff > 0) {
               pushDelta(groupBucketId, this.makeDeltaFromLeg(groupBucketId, end, 'Increase', endSide, diff));
            } else if (diff < 0) {
               pushDelta(groupBucketId, this.makeDeltaFromLeg(groupBucketId, end, 'Decrease', endSide * -1, -diff));
            }
            // diff === 0 -> no transaction leg.
         } else {
            // Rule 4: reverse -> Close the current side first, then Open the end-state side.
            pushDelta(groupBucketId, this.makeDelta(p.groupId || '', p, 'Close', curSide * -1, curQty));
            pushDelta(groupBucketId, this.makeDeltaFromLeg(groupBucketId, end, 'Open', endSide, endQty));
         }
      });

      // Walk end-state legs that have no matching current position: Rule 1 -> Open.
      endLegs.forEach(l => {
         const key = this.positionKey(l.groupBucketId, l.tradingInstrumentCode);
         if (currentByKey.has(key)) { return; }   // matched above (both / reverse / inc-dec)
         const endQty = Math.abs(this.endStateQty(l));
         pushDelta(l.groupBucketId, this.makeDeltaFromLeg(l.groupBucketId, l, 'Open', l.side, endQty));
      });

      return groupOrder.map(groupBucketId => ({
         groupBucketId,
         deltas: deltasByGroup.get(groupBucketId)
      }));
   }

   /** Build a delta from a current position (Close path carries instrument identity from the live row). */
   private makeDelta(
      groupBucketId: string,
      p: CollarsPositionDto,
      action: 'Open' | 'Close' | 'Increase' | 'Decrease',
      side: number,
      qty: number
   ): CollarsTransactionDelta {
      const delta: CollarsTransactionDelta = {
         groupBucketId,
         tradingInstrumentCode: p.tradingInstrumentCode,
         kind: p.kind,
         action,
         side,
         qty
      };
      // Current positions carry no strike/optionType/expiration on the DTO; they live in the ticker code.
      return delta;
   }

   /** Build a delta from an end-state editable leg (Open / Increase / Decrease path carries option attrs). */
   private makeDeltaFromLeg(
      groupBucketId: string,
      leg: CollarsEditableLeg,
      action: 'Open' | 'Close' | 'Increase' | 'Decrease',
      side: number,
      qty: number
   ): CollarsTransactionDelta {
      const delta: CollarsTransactionDelta = {
         groupBucketId,
         tradingInstrumentCode: leg.tradingInstrumentCode,
         kind: leg.kind,
         action,
         side,
         qty
      };
      if (this.isPresent(leg.strike)) { delta.strike = leg.strike; }
      if (!isVoid(leg.optionType)) { delta.optionType = leg.optionType; }
      if (!isVoid(leg.expiration)) { delta.expiration = leg.expiration; }
      return delta;
   }
}
